import React, { FC, useEffect, useMemo, useState } from 'react'
import Modal from '../common/Modal'
import { labelColor } from '../../../utils/taskStatusLabel'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

interface TargetList {
  member_id: number
  member_name: string
  member_image: string | null
  role_id: number
}

interface UpdateParam {
  role_id: number
  target_list: TargetList[]
}

interface Role {
  id: number
  name: string
}

interface Props {
  targetList: TargetList[]
  roleList: Role[]
  currentMemberId: number
  closeCallback: VoidFunction
  updateCallback: (param: UpdateParam, callback: VoidFunction) => void
  isDisplay: boolean
  closeable?: boolean
  isApplication?: boolean
}

const MultiRoleSettingModal: FC<Props> = (props) => {
  const {
    targetList,
    roleList,
    currentMemberId,
    closeCallback,
    updateCallback,
    isDisplay,
    closeable,
    isApplication,
  } = props

  const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false)
  const [selectRoleId, setSelectRoleId] = useState<number>(0)

  const displayWord = useMemo(() => {
    if (isApplication) return { text: "申請", status: "processing" }
    return { text: "承認", status: "approved" }
  }, [isApplication])

  const closeModal = () => {
    closeCallback()
    setSelectRoleId(0)
  }

  const submitApproval = () => {
    setIsSubmitLoading(true)
    const param = {
      role_id: selectRoleId,
      target_list: targetList,
    }
    updateCallback(
      param,
      () => {
        setIsSubmitLoading(false)
        closeModal()
      },
    )
  }

  useEffect(() => {
    setSelectRoleId(targetList[0]?.role_id || 0)
  }, [targetList])

  return (
    <Modal
      show={isDisplay}
      wrapperClass="multi_approved_modal w750"
      closeCallback={closeModal}
      closeable={closeable && !isSubmitLoading}
    >
      <>
        <div className="multi_approved_modal_title">
          まとめて権限ロール設定
        </div>
        <div className="multi_approved_modal_main">
          <div
            className="description"
            style={{
              marginBottom: "24px",
            }}
          >
            複数ユーザーの権限ロール設定します
          </div>
          <div
            style={{
              marginBottom: "4px",
            }}
          >
            対象ユーザー
          </div>
          {
            targetList.length ? (
              <>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "4px",
                    border: "1px solid #cccccc",
                    padding: "8px",
                    marginBottom: "8px",
                  }}
                >
                  {
                    targetList.map((v) => (
                      <React.Fragment key={`target_${v.member_id}`}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "8px",
                            border: "solid 1px #CCCCCC",
                            borderRadius: "20px",
                            padding: "4px 8px",
                          }}
                        >
                          <img
                            alt={v.member_name}
                            src={v.member_image ?? `/static/images/profile/${(Number(v.member_id) || 0) % 12}.jpeg`}
                            style={{ width: "16px", height: "16px", borderRadius: "50%" }}
                          />
                          <div
                            style={{
                              display: "flex",
                              flexFlow: "column",
                              justifyContent: "center",
                              alignItems: "flex-start",
                            }}
                          >
                            {v.member_name}
                          </div>
                        </div>
                      </React.Fragment>
                    ))
                  }
                </div>
                {
                  !!targetList.find((v) => v.member_id === currentMemberId) && (
                    <div
                      style={{
                        border: "solid 1px #ff0000",
                        borderRadius: "4px",
                        padding: "8px",
                        backgroundColor: "#ffeaea",
                        color: "#FF0000",
                        marginBottom: "8px",
                      }}
                    >
                      自身のアカウントが含まれています。ご注意ください。
                    </div>
                  )
                }
              </>
            ) : (
              <>
                <div
                  style={{
                    textAlign: "center",
                    fontSize: "22px",
                    fontWeight: "bold",
                    color: "#999999",
                    padding: "8px",
                  }}
                >
                  対象のユーザーがいません
                </div>
              </>
            )
          }
          <div
            style={{
              marginBottom: "4px",
            }}
          >
            設定する権限ロール
          </div>
          <div
            style={{
              position: "relative",
              border: "solid 1px #CCCCCC",
              borderRadius: "4px",
            }}
          >
            <select
              style={{
                width: "100%",
                padding: "8px",
              }}
              onChange={(e) => setSelectRoleId(Number(e.target.value))}
              value={selectRoleId}
            >
              {
                roleList.map((v) => (
                  <option value={v.id}>
                    {v.name}
                  </option>
                ))
              }
            </select>
            <div
              style={{
                position: "absolute",
                top: "50%",
                right: "8px",
                transform: "translateY(-50%)",
                pointerEvents: "none",
              }}
            >
              <FontAwesomeIcon icon={['fas', 'caret-down']} />
            </div>
          </div>
          <div className="button_area">
            <button
              type="button"
              onClick={closeModal}
              disabled={isSubmitLoading}
            >
              キャンセル
            </button>
            <button
              type="button"
              style={{
                border: `solid 1px ${labelColor(!targetList.length ? "" : displayWord.status)}`,
                backgroundColor: labelColor(!targetList.length ? "" : displayWord.status),
                color: "#FFFFFF",
              }}
              onClick={() => submitApproval()}
              disabled={isSubmitLoading || !targetList.length}
            >
              {
                isSubmitLoading ? (
                  <>
                    <FontAwesomeIcon icon={['fas', 'spinner']} spin />
                  </>
                ) : (
                  <>
                    変更する
                  </>
                )
              }
            </button>
          </div>
        </div>
      </>
    </Modal>
  )
}

MultiRoleSettingModal.defaultProps = {
  closeable: true,
  isApplication: false,
}

export default MultiRoleSettingModal
